import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  useTheme,
  Avatar,
  FormControlLabel,
  Radio,
  RadioGroup,
  alpha,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Cancel as CancelIcon,
  Category as CategoryIcon,
  Receipt,
  CheckCircle,
  Warning,
  Settings,
  Add,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import SysDateRangePicker from '../components/SysDateRangePicker';
import Swal from 'sweetalert2';

import { Despesa, DespesasFilters, DespesasStats, Category } from '../types';
import despesasService from '../services/despesasService';
import categoryService from '../services/categoryService';
import { useAuth, usePermissions } from '../contexts/AuthContext';
import CategoryManager from '../components/CategoryManager';
import DespesasForm from '../components/forms/DespesasForm';
import DespesasMultipleForm from '../components/forms/DespesasMultipleForm';
import DespesasPaymentForm from '../components/forms/DespesasPaymentForm';
import EscritorioSelect from '../components/selects/EscritorioSelect';
import { formatarValor, formatarData } from '../utils/formatToView';
import { useRelatorioCores } from './relatorios';

// Importar ícones do Material-UI
import * as Icons from '@mui/icons-material';


const getTipoLabel = (tipo: string): string => {
  const labels = {
    'fixa': 'Fixa',
    'variavel': 'Variável'
  };
  return labels[tipo as keyof typeof labels] || tipo;
};

// Função para determinar o status dinâmico baseado na data de vencimento
const getDynamicStatus = (status: string, dataVencimento: string): string => {
  // Se já está pago ou pendente de cancelamento, mantém o status original
  if (status === 'pago' || status === 'pendente_delete') return status;

  try {
    console.log('=== DEBUG getDynamicStatus ===');
    console.log('Status original:', status);
    console.log('Data vencimento recebida:', dataVencimento);
    console.log('Tipo da data vencimento:', typeof dataVencimento);

    // Criar data de hoje e resetar para meia-noite
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    // Para a data de vencimento, vamos criar uma data local sem considerar fuso horário
    // Dividir a string "YYYY-MM-DD" e criar uma data local
    const [ano, mes, dia] = dataVencimento.split('-').map(Number);
    const vencimento = new Date(ano, mes - 1, dia); // mes - 1 porque Date() usa 0-11 para meses

    console.log('Data hoje (meia-noite local):', hoje);
    console.log('Data vencimento (meia-noite local):', vencimento);
    console.log('Data hoje getTime():', hoje.getTime());
    console.log('Data vencimento getTime():', vencimento.getTime());
    console.log('São iguais?', hoje.getTime() === vencimento.getTime());

    // Se a data de vencimento é igual a hoje, retorna "vencendo_hoje"
    if (vencimento.getTime() === hoje.getTime()) {
      console.log('✅ Status definido como: vencendo_hoje');
      return 'vencendo_hoje';
    }

    // Se a data de vencimento é anterior a hoje, verifica se é ontem
    if (vencimento < hoje) {
      // Calcular a diferença em dias
      const diffTime = hoje.getTime() - vencimento.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 1) {
        console.log('✅ Status definido como: venceu_ontem');
        return 'venceu_ontem';
      } else {
        console.log('✅ Status definido como: vencido');
        return 'vencido';
      }
    }

    // Se a data de vencimento é posterior a hoje, retorna "pendente"
    console.log('✅ Status definido como: pendente');
    return 'pendente';
  } catch (error) {
    console.error('Erro ao calcular status dinâmico:', error);
    return status; // Em caso de erro, retorna o status original
  }
};

const getStatusLabel = (status: string): string => {
  const labels = {
    'pendente': 'Pendente',
    'pago': 'Pago',
    'vencido': 'Vencido',
    'vencendo_hoje': 'Vencendo Hoje',
    'venceu_ontem': 'Venceu Ontem',
    'pendente_delete': 'Pendente de Cancelamento'
  };
  return labels[status as keyof typeof labels] || status;
};

const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const colors = {
    'pendente': 'info' as const,
    'pago': 'success' as const,
    'vencido': 'error' as const,
    'vencendo_hoje': 'warning' as const,
    'venceu_ontem': 'error' as const,
    'pendente_delete': 'default' as const
  };
  return colors[status as keyof typeof colors] || 'default';
};

// Tipos para filtros avançados
interface AdvancedExpenseFilters extends Omit<DespesasFilters, 'mes' | 'ano'> {
  periodo_tipo: 'mes_especifico' | 'periodo_personalizado' | 'todos_meses';
  mes?: number;
  ano?: number;
  data_inicio?: string;
  data_fim?: string;
  escritorio_id?: number | null;
}

const DespesasPage: React.FC = () => {
  const theme = useTheme();
  const cores = useRelatorioCores();
  const { user } = useAuth();
  const { can } = usePermissions();

  // Estados principais
  const [expenses, setExpenses] = useState<Despesa[]>([]);
  const [stats, setStats] = useState<DespesasStats | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  // Estados para filtros avançados
  const [filters, setFilters] = useState<AdvancedExpenseFilters>({
    periodo_tipo: 'mes_especifico',
    mes: new Date().getMonth() + 1,
    ano: new Date().getFullYear(),
    status: 'todos',
    categoria: 'todas',
    tipo: 'todos',
    escritorio_id: null
  });

  // Estados para formulário e modais
  const [formOpen, setFormOpen] = useState(false);
  const [multipleFormOpen, setMultipleFormOpen] = useState(false);
  const [paymentFormOpen, setPaymentFormOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Despesa | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);

  // Carregar dados
  const loadDespesas = useCallback(async () => {
    try {
      setLoading(true);

      // Preparar filtros para a API
      const apiFilters: any = {
        categoria: filters.categoria !== 'todas' ? filters.categoria : undefined,
        tipo: filters.tipo !== 'todos' ? filters.tipo : undefined,
        escritorio_id: filters.escritorio_id || undefined
      };

      // Tratar filtro de status especial para "vencendo hoje"
      if (filters.status === 'vencendo_hoje') {
        // Para "vencendo hoje", filtrar por data e excluir despesas pagas
        const hoje = new Date();
        const dataHoje = hoje.toISOString().split('T')[0];
        apiFilters.data_vencimento = dataHoje;
        apiFilters.status_exclude = 'pago'; // Excluir despesas pagas
      } else if (filters.status === 'venceu_ontem') {
        // Para "venceu ontem", filtrar por data de vencimento de ontem
        const ontem = new Date();
        ontem.setDate(ontem.getDate() - 1);
        ontem.setHours(0, 0, 0, 0);
        apiFilters.data_vencimento = ontem.toISOString().split('T')[0];
        apiFilters.status_exclude = 'pago'; // Excluir despesas pagas
      } else if (filters.status !== 'todos') {
        // Para outros status, aplicar o filtro normal
        apiFilters.status = filters.status;
      }

      // Aplicar filtros de período baseado no tipo selecionado
      switch (filters.periodo_tipo) {
        case 'mes_especifico':
          if (filters.mes && filters.ano) {
            apiFilters.mes = filters.mes;
            apiFilters.ano = filters.ano;
          }
          break;
        case 'periodo_personalizado':
          if (filters.data_inicio && filters.data_fim) {
            apiFilters.data_inicio = filters.data_inicio;
            apiFilters.data_fim = filters.data_fim;
          }
          break;
        case 'todos_meses':
          // Não aplicar filtro de período - buscar todas as despesas
          break;
      }

      const response = await despesasService.getDespesas(apiFilters);

      if (response.success && response.data) {
        setExpenses(response.data);
        if (response.stats) {
          setStats(response.stats);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar despesas:', error);
      showAlert('error', 'Erro ao carregar despesas');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await categoryService.getActiveCategories();
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  }, []);

  useEffect(() => {
    loadDespesas();
  }, [loadDespesas]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const showAlert = (type: 'success' | 'error', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  // Manipuladores de filtros
  const handleFilterChange = (field: keyof AdvancedExpenseFilters, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => {
    setFilters({
      periodo_tipo: 'mes_especifico',
      mes: new Date().getMonth() + 1,
      ano: new Date().getFullYear(),
      status: 'todos',
      categoria: 'todas',
      tipo: 'todos',
      escritorio_id: null
    });
  };

  // Manipuladores de ações
  const handleEdit = (expense: Despesa) => {
    setSelectedExpense(expense);
    setFormOpen(true);
  };

  const handleCancel = async (expense: Despesa) => {
    // Se já estiver pendente de cancelamento, mostrar detalhes
    if (expense.status === 'pendente_delete') {
      const userInfo = expense.userId_pediu_delete ? `Usuário ID: ${expense.userId_pediu_delete}` : 'Usuário não identificado';
      const dataInfo = expense.data_pediu_delete ? `Data: ${new Date(expense.data_pediu_delete).toLocaleDateString('pt-BR')}` : 'Data não informada';

      Swal.fire({
        title: 'Detalhes da Solicitação de Cancelamento',
        html: `
          <div style="text-align: left;">
            <p><strong>Motivo:</strong> ${expense.motivo_delete || 'Não informado'}</p>
            <p><strong>${userInfo}</strong></p>
            <p><strong>${dataInfo}</strong></p>
          </div>
        `,
        icon: 'info',
        confirmButtonText: 'OK',
        confirmButtonColor: '#1976d2'
      });
      return;
    }

    // Solicitar motivo do cancelamento
    const { value: motivoCancel } = await Swal.fire({
      title: 'Solicitar Cancelamento',
      input: 'textarea',
      inputLabel: 'Motivo do cancelamento',
      inputPlaceholder: 'Digite o motivo do cancelamento...',
      inputValidator: (value) => {
        if (!value || value.trim().length === 0) {
          return 'O motivo do cancelamento é obrigatório!';
        }
        if (value.trim().length > 1000) {
          return 'O motivo deve ter no máximo 1000 caracteres!';
        }
        return null;
      },
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#757575',
      inputAutoFocus: true
    });

    if (motivoCancel) {
      try {
        setLoading(true);
        const response = await despesasService.cancelarDespesa(expense.id, motivoCancel);

        if (response.success) {
          Swal.fire({
            title: 'Sucesso!',
            text: 'Solicitação de cancelamento enviada com sucesso',
            icon: 'success',
            confirmButtonText: 'OK'
          });

          // Recarregar a lista
          loadDespesas();
        } else {
          Swal.fire({
            title: 'Erro!',
            text: response.message || 'Erro ao solicitar cancelamento',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      } catch (error) {
        console.error('Erro ao solicitar cancelamento:', error);
        Swal.fire({
          title: 'Erro!',
          text: 'Erro interno ao solicitar cancelamento',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (expense: Despesa) => {
    const result = await Swal.fire({
      title: 'Deletar Despesa',
      text: `Tem certeza que deseja deletar a despesa "${expense.descricao}"? Esta ação não pode ser desfeita.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, deletar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#757575'
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      const response = await despesasService.deletarDespesa(expense.id);

      if (response.success) {
        Swal.fire({
          title: 'Deletada!',
          text: 'Despesa deletada com sucesso',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        loadDespesas();
      } else {
        Swal.fire({
          title: 'Erro!',
          text: response.message || 'Erro ao deletar despesa',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Erro ao deletar despesa:', error);
      Swal.fire({
        title: 'Erro!',
        text: 'Erro interno ao deletar despesa',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = (expense: Despesa) => {
    setSelectedExpense(expense);
    setPaymentFormOpen(true);
  };

  const handlePaymentSuccess = async (paymentData: {
    data_pagamento: string;
    valor_pago: number;
    forma_pgto: string;
    caixa_financeiro_id: number | null;
    observacoes?: string;
  }) => {
    if (!selectedExpense) return;

    // Validar se o caixa foi selecionado
    if (!paymentData.caixa_financeiro_id) {
      showAlert('error', 'Selecione um caixa financeiro para registrar o pagamento');
      return;
    }

    try {
      setLoading(true);
      const response = await despesasService.pagarDespesa(selectedExpense.id, {
        ...paymentData,
        caixa_financeiro_id: paymentData.caixa_financeiro_id
      });

      if (response.success) {
        showAlert('success', 'Despesa marcada como paga com sucesso!');
        loadDespesas();
        setPaymentFormOpen(false);
        setSelectedExpense(null);
      } else {
        showAlert('error', response.message || 'Erro ao marcar como paga');
      }
    } catch (error: any) {
      showAlert('error', error.message || 'Erro ao marcar como paga');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPayment = async (expense: Despesa) => {
    //use um sweetalert para confirmar a ação
    const { value: confirm } = await Swal.fire({
      title: 'Cancelar Pagamento',
      text: 'Tem certeza que deseja cancelar o pagamento?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#757575'
    });

    if (confirm) {
      try {
        const response = await despesasService.cancelarPagamento(expense.id);
        if (response.success) {
          showAlert('success', 'Pagamento cancelado!');
          loadDespesas();
        }
      } catch (error: any) {
        showAlert('error', error.message || 'Erro ao cancelar pagamento');
      }
    }
  };

  const handleFormSuccess = () => {
    showAlert('success', selectedExpense ? 'Despesa atualizada com sucesso!' : 'Despesa criada com sucesso!');
    loadDespesas();
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedExpense(null);
  };

  const handlePaymentFormClose = () => {
    setPaymentFormOpen(false);
    setSelectedExpense(null);
  };

  // Buscar categoria por código
  const getCategoryByCode = (codigo: string): Category | undefined => {
    return categories.find(cat => cat.codigo === codigo);
  };

  // Renderizar ícone de categoria
  const renderCategoryIcon = (categoria: string, size: 'small' | 'medium' = 'small') => {
    const categoryData = getCategoryByCode(categoria);
    if (categoryData?.icone) {
      const IconComponent = (Icons as any)[categoryData.icone] || Icons.Category;
      return <IconComponent fontSize={size} />;
    }
    return <CategoryIcon fontSize={size} />;
  };

  // Renderizar widgets de estatísticas
  const renderStatsWidgets = () => {
    if (!stats) return null;

    const widgets = [
      {
        title: 'Total do Período',
        value: formatarValor(stats.total.value),
        count: `${stats.total.count} despesas`,
        icon: <Receipt />,
        color: 'primary'
      },
      {
        title: 'Despesas Pagas',
        value: formatarValor(stats.paid.value),
        count: `${stats.paid.count} despesas`,
        icon: <CheckCircle />,
        color: 'success'
      },
      {
        title: 'Despesas Pendentes',
        value: formatarValor(stats.pending.value),
        count: `${stats.pending.count} despesas`,
        icon: <Warning />,
        color: 'warning'
      },
      {
        title: 'Despesas Vencidas',
        value: formatarValor(stats.overdue.value),
        count: `${stats.overdue.count} despesas`,
        icon: <CancelIcon />,
        color: 'error'
      }
    ];

    return (
      <Grid container spacing={1} sx={{ mb: 1 }}>
        {widgets.map((widget, index) => (
          <Grid item xs={6} sm={6} md={3} key={index}>
            <Card>
              <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: '0.8rem', mb: 0.25 }}>
                      {widget.title}
                    </Typography>
                    <Typography variant="h5" component="div" sx={{ fontSize: '1.2rem', lineHeight: 1.2, mb: 0.25 }}>
                      {widget.value}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                      {widget.count}
                    </Typography>
                  </Box>
                  <Box color={`${widget.color}.main`} sx={{ '& svg': { fontSize: 32 } }}>
                    {widget.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Renderizar filtros de período
  const renderPeriodFilters = () => (
    <Grid container spacing={2} alignItems="center">
      {/* Tipo de Período */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem', mb: 0.5 }}>
          Período
        </Typography>
        <RadioGroup
          row
          value={filters.periodo_tipo}
          onChange={(e) => handleFilterChange('periodo_tipo', e.target.value)}
        >
          <FormControlLabel
            value="mes_especifico"
            control={<Radio size="small" />}
            label="Mês Específico"
          />
          <FormControlLabel
            value="periodo_personalizado"
            control={<Radio size="small" />}
            label="Período Personalizado"
          />
          <FormControlLabel
            value="todos_meses"
            control={<Radio size="small" />}
            label="Todos os Meses"
          />
        </RadioGroup>
      </Grid>

      {/* Filtros condicionais baseados no tipo de período */}
      {filters.periodo_tipo === 'mes_especifico' && (
        <>
          <Grid item xs={12} sm={3} md={2}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: alpha(theme.palette.divider, 0.05),
              borderRadius: 2,
              p: 0.5,
              border: `1px solid ${theme.palette.divider}`,
              height: 40
            }}>
              <IconButton
                size="small"
                onClick={() => handleFilterChange('ano', (filters.ano || new Date().getFullYear()) - 1)}
                sx={{ color: theme.palette.primary.main }}
              >
                <ChevronLeft />
              </IconButton>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: theme.palette.text.primary }}>
                {filters.ano}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleFilterChange('ano', (filters.ano || new Date().getFullYear()) + 1)}
                sx={{ color: theme.palette.primary.main }}
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12} sm={9} md={10}>
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              bgcolor: alpha(theme.palette.divider, 0.05),
              p: 0.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              justifyContent: 'center'
            }}>
              {['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'].map((month, index) => (
                <Button
                  key={month}
                  variant={filters.mes === index + 1 ? 'contained' : 'text'}
                  size="small"
                  onClick={() => handleFilterChange('mes', index + 1)}
                  sx={{
                    minWidth: { xs: 'calc(25% - 4px)', sm: 'auto' },
                    px: { xs: 1, sm: 2 },
                    height: 32,
                    borderRadius: 1.5,
                    fontWeight: filters.mes === index + 1 ? 800 : 600,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: filters.mes === index + 1 ? '#fff' : theme.palette.text.secondary,
                    boxShadow: filters.mes === index + 1 ? theme.shadows[2] : 'none',
                    '&:hover': {
                      bgcolor: filters.mes === index + 1 ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.08),
                    }
                  }}
                >
                  {month}
                </Button>
              ))}
            </Box>
          </Grid>
        </>
      )}

      {filters.periodo_tipo === 'periodo_personalizado' && (
        <Grid item xs={12} sm={12} md={6}>
          <SysDateRangePicker
            onChange={(range: { startDate: Date | null; endDate: Date | null }) => {
              const startStr = range.startDate ? new Date(range.startDate.getTime() - range.startDate.getTimezoneOffset() * 60000).toISOString().split('T')[0] : '';
              const endStr = range.endDate ? new Date(range.endDate.getTime() - range.endDate.getTimezoneOffset() * 60000).toISOString().split('T')[0] : '';
              handleFilterChange('data_inicio', startStr);
              handleFilterChange('data_fim', endStr);
            }}
          />
        </Grid>
      )}
    </Grid>
  );

  return (
    <Box sx={{ p: 0.5 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 1,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(cores.primario, 0.04)} 0%, ${alpha(cores.secundario, 0.04)} 100%)`,
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(cores.primario, 0.12), color: cores.primario, width: 40, height: 40 }}>
              <Receipt sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Gestão de Despesas
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Controle todas as despesas do sistema
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Settings sx={{ fontSize: 16 }} />}
              onClick={() => setCategoryManagerOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                borderRadius: 1.5,
              }}
            >
              Gerenciar Categorias
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ReceiptIcon sx={{ fontSize: 16 }} />}
              onClick={() => {
                setSelectedExpense(null);
                setMultipleFormOpen(true);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                borderRadius: 1.5,
              }}
            >
              Múltiplas Despesas
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add sx={{ fontSize: 16 }} />}
              onClick={() => {
                setSelectedExpense(null);
                setFormOpen(true);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                borderRadius: 1.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: theme.shadows[2] },
              }}
            >
              Nova Despesa
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Alert */}
      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2, fontSize: '0.8rem' }}>
          {alert.message}
        </Alert>
      )}

      {/* Filtros */}
      <Paper sx={{ p: 1, mb: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', mb: 0.5 }}>
          Filtros
        </Typography>

        {/* Filtros de Período */}
        {renderPeriodFilters()}

        {/* Outros Filtros */}
        <Grid container spacing={1.5} alignItems="center" sx={{ mt: 1.5 }}>
          <Grid item xs={6} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || 'todos'}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="pendente">Pendente</MenuItem>
                <MenuItem value="pago">Pago</MenuItem>
                <MenuItem value="vencido">Vencido</MenuItem>
                <MenuItem value="vencendo_hoje">Vencendo Hoje</MenuItem>
                <MenuItem value="venceu_ontem">Venceu Ontem</MenuItem>
                <MenuItem value="pendente_delete">Pendente de Cancelamento</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={filters.tipo || 'todos'}
                label="Tipo"
                onChange={(e) => handleFilterChange('tipo', e.target.value)}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="fixa">Fixa</MenuItem>
                <MenuItem value="variavel">Variável</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoria</InputLabel>
              <Select
                value={filters.categoria || 'todas'}
                label="Categoria"
                onChange={(e) => handleFilterChange('categoria', e.target.value)}
              >
                <MenuItem value="todas">Todas</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.codigo}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        sx={{
                          width: 20,
                          height: 20,
                          bgcolor: category.cor || '#1976d2',
                          fontSize: '0.75rem'
                        }}
                      >
                        {renderCategoryIcon(category.codigo, 'small')}
                      </Avatar>
                      {category.nome}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <EscritorioSelect
              value={filters.escritorio_id}
              onChange={(value) => handleFilterChange('escritorio_id', value)}
              label="Escritório"
              size="small"
              required={false}
              allowClear={true}
              multiple={false}
              onlyActive={true}
              showTodosOption={true}
              placeholder="Todos os escritórios"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              onClick={resetFilters}
              fullWidth
              size="small"
            >
              Limpar Filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Widgets de estatísticas */}
      {renderStatsWidgets()}

      {/* Tabela de despesas */}
      <Paper elevation={2} sx={{ p: 1.5, borderRadius: 2, overflow: 'hidden' }}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
          Lista de Despesas
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (t) => `color-mix(in srgb, ${theme.palette.primary.main} 12%, ${t.palette.background.paper})`,
                  fontWeight: 700,
                  fontSize: '0.73rem',
                  py: 0.8,
                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              }}>
                <TableCell>Descrição</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Escritório</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Vencimento</TableCell>

                <TableCell>Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : expenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Nenhuma despesa encontrada
                  </TableCell>
                </TableRow>
              ) : (
                expenses.map((expense) => {
                  const categoryData = getCategoryByCode(expense.categoria);
                  const dynamicStatus = getDynamicStatus(expense.status, expense.data_vencimento);
                  return (
                    <TableRow key={expense.id} hover sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}>
                      <TableCell sx={{ whiteSpace: 'nowrap', py: 0.5, fontSize: '0.78rem' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          {expense.descricao}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', py: 0.5, fontSize: '0.78rem' }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <Avatar
                            sx={{
                              width: 20,
                              height: 20,
                              bgcolor: categoryData?.cor || '#1976d2',
                              fontSize: '0.65rem'
                            }}
                          >
                            {renderCategoryIcon(expense.categoria, 'small')}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {categoryData?.nome || expense.categoria}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', py: 0.5, fontSize: '0.78rem' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          <BusinessIcon fontSize="small" color="primary" /> {expense.escritorio?.identificacao || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Chip
                          label={getTipoLabel(expense.tipo)}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                          {formatarValor(expense.valor)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {formatarData(expense.data_vencimento)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Chip
                          label={getStatusLabel(dynamicStatus)}
                          size="small"
                          color={getStatusColor(dynamicStatus)}
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Box display="flex" gap={0.5} justifyContent="center">
                          {dynamicStatus !== 'pendente_delete' && dynamicStatus !== 'pago' && (
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(expense)}
                                sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {dynamicStatus !== 'pago' && dynamicStatus !== 'pendente_delete' ? (
                            <Tooltip title="Marcar como Paga">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleMarkAsPaid(expense)}
                                sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                              >
                                <PaymentIcon />
                              </IconButton>
                            </Tooltip>
                          ) : dynamicStatus === 'pago' ? (
                            <Tooltip title="Cancelar Pagamento">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleCancelPayment(expense)}
                                sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {dynamicStatus !== 'pendente_delete' && dynamicStatus !== 'pago' && (
                            <Tooltip title={dynamicStatus === 'pendente_delete' ? 'Ver Detalhes do Cancelamento' : 'Solicitar Cancelamento'}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleCancel(expense)}
                                sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {can('financeiro.contas_pagar.excluir') && dynamicStatus !== 'pago' && (
                            <Tooltip title="Deletar Despesa">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(expense)}
                                sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>


      {/* Modal de formulário de despesa */}
      <DespesasForm
        open={formOpen}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        selectedExpense={selectedExpense}
        categories={categories}
      />

      {/* Modal de formulário de múltiplas despesas */}
      <DespesasMultipleForm
        open={multipleFormOpen}
        onClose={() => setMultipleFormOpen(false)}
        onSuccess={handleFormSuccess}
        selectedExpense={selectedExpense}
        categories={categories}
        title="Nova Despesa Múltipla"
      />

      {/* Modal de gerenciamento de categorias */}
      <CategoryManager
        open={categoryManagerOpen}
        onClose={() => setCategoryManagerOpen(false)}
        onCategoryChange={() => {
          loadCategories();
          loadDespesas();
        }}
      />

      {/* Modal de pagamento de despesa */}
      <DespesasPaymentForm
        open={paymentFormOpen}
        onClose={handlePaymentFormClose}
        onSuccess={handlePaymentSuccess}
        despesa={selectedExpense}
      />
    </Box>
  );
};

export default DespesasPage; 